import React, { useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import EventMap from '../components/EventMap';
import { apiJson } from '../utils/api';

interface AdminEvent {
  event_id: string;
  name: string;
  start_date: string;
  end_date: string;
  location_name?: string;
  airport_code?: string;
  website?: string;
  lat?: number;
  lng?: number;
  radius_km?: number;
  road_ahead_event_id?: string;
  status?: string;
}

interface EventForm {
  name: string;
  start_date: string;
  end_date: string;
  location_name: string;
  airport_code: string;
  lat: string;
  lng: string;
  radius_km: string;
  road_ahead_event_id: string;
  website: string;
}

const EMPTY: EventForm = {
  name: '',
  start_date: '',
  end_date: '',
  location_name: '',
  airport_code: '',
  lat: '',
  lng: '',
  radius_km: '',
  road_ahead_event_id: '',
  website: '',
};

type FieldHandler = (key: keyof EventForm) => (e: { detail: { value: string } }) => void;

/** The shared event field grid, used by both the create form and edit modal. */
const FieldGrid: React.FC<{ form: EventForm; onField: FieldHandler }> = ({ form, onField }) => (
  <ColumnLayout columns={2}>
    <FormField label="Name">
      <Input value={form.name} onChange={onField('name')} placeholder="Regional Fly-In 2026" />
    </FormField>
    <FormField label="Website">
      <Input value={form.website} onChange={onField('website')} placeholder="https://…" />
    </FormField>
    <FormField label="Start date">
      <Input value={form.start_date} onChange={onField('start_date')} placeholder="2026-07-20" />
    </FormField>
    <FormField label="End date">
      <Input value={form.end_date} onChange={onField('end_date')} placeholder="2026-07-26" />
    </FormField>
    <FormField label="Location">
      <Input
        value={form.location_name}
        onChange={onField('location_name')}
        placeholder="Oshkosh, WI"
      />
    </FormField>
    <FormField label="Airport code">
      <Input value={form.airport_code} onChange={onField('airport_code')} placeholder="KOSH" />
    </FormField>
    <FormField label="Latitude">
      <Input value={form.lat} onChange={onField('lat')} placeholder="43.9844" />
    </FormField>
    <FormField label="Longitude">
      <Input value={form.lng} onChange={onField('lng')} placeholder="-88.557" />
    </FormField>
    <FormField
      label="Geo-fence radius (km)"
      description="Auto-tags sightings within this radius; blank uses the default 15 km."
    >
      <Input value={form.radius_km} onChange={onField('radius_km')} placeholder="15" type="number" />
    </FormField>
    <FormField
      label="Road Ahead event ID"
      description="Maps this fly-in to a Road Ahead event so users can share parked-aircraft spots to its community layer (e.g. airventure-2026). Blank disables sharing."
    >
      <Input
        value={form.road_ahead_event_id}
        onChange={onField('road_ahead_event_id')}
        placeholder="airventure-2026"
      />
    </FormField>
  </ColumnLayout>
);

const toForm = (e: AdminEvent): EventForm => ({
  name: e.name,
  start_date: e.start_date,
  end_date: e.end_date,
  location_name: e.location_name ?? '',
  airport_code: e.airport_code ?? '',
  lat: e.lat != null ? String(e.lat) : '',
  lng: e.lng != null ? String(e.lng) : '',
  radius_km: e.radius_km != null ? String(e.radius_km) : '',
  road_ahead_event_id: e.road_ahead_event_id ?? '',
  website: e.website ?? '',
});

/** Trims empty strings and turns lat/lng into numbers for the API. */
const toBody = (form: EventForm) => ({
  name: form.name,
  start_date: form.start_date,
  end_date: form.end_date,
  location_name: form.location_name || undefined,
  airport_code: form.airport_code || undefined,
  website: form.website || undefined,
  lat: form.lat ? Number(form.lat) : undefined,
  lng: form.lng ? Number(form.lng) : undefined,
  radius_km: form.radius_km ? Number(form.radius_km) : undefined,
  road_ahead_event_id: form.road_ahead_event_id.trim() || undefined,
});

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<EventForm>(EMPTY);
  const [editing, setEditing] = useState<AdminEvent | null>(null);
  const [editForm, setEditForm] = useState<EventForm>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson<{ items: AdminEvent[] }>('/events?window=all');
      setEvents(data.items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const seed = async (): Promise<void> => {
    setSeeding(true);
    setMessage(null);
    try {
      const data = await apiJson<{ message: string }>('/admin/events/seed', { method: 'POST' });
      setMessage(data.message);
      await load();
    } catch (e) {
      setMessage(`Error: ${(e as Error).message}`);
    } finally {
      setSeeding(false);
    }
  };

  const create = async (): Promise<void> => {
    setCreating(true);
    setMessage(null);
    try {
      await apiJson('/admin/events', { method: 'POST', body: JSON.stringify(toBody(form)) });
      setMessage(`Created "${form.name}".`);
      setForm(EMPTY);
      await load();
    } catch (e) {
      setMessage(`Error: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!editing) return;
    setSaving(true);
    setMessage(null);
    try {
      await apiJson(`/admin/events/${encodeURIComponent(editing.event_id)}`, {
        method: 'PUT',
        body: JSON.stringify(toBody(editForm)),
      });
      setMessage(`Updated "${editForm.name}".`);
      setEditing(null);
      await load();
    } catch (e) {
      setMessage(`Error: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const field: FieldHandler = (key) => (e) => setForm((f) => ({ ...f, [key]: e.detail.value }));
  const editField: FieldHandler = (key) => (e) =>
    setEditForm((f) => ({ ...f, [key]: e.detail.value }));

  const canCreate = form.name && form.start_date && form.end_date && !creating;
  const canSave = editForm.name && editForm.start_date && editForm.end_date && !saving;

  // Map center + radius from the edit form (fall back to a US-center view and
  // the default 15 km fence when the event has no coordinates yet).
  const mapLat =
    editForm.lat !== '' && Number.isFinite(Number(editForm.lat)) ? Number(editForm.lat) : 39.5;
  const mapLng =
    editForm.lng !== '' && Number.isFinite(Number(editForm.lng)) ? Number(editForm.lng) : -98.35;
  const mapRadius = Number(editForm.radius_km) > 0 ? Number(editForm.radius_km) : 15;

  return (
    <ContentLayout header={<Header variant="h1">Fly-in Events</Header>}>
      <SpaceBetween size="l">
        {message && (
          <Alert type={message.startsWith('Error') ? 'error' : 'success'}>{message}</Alert>
        )}

        <Container
          header={
            <Header
              variant="h2"
              description="Insert a few well-known fly-ins (EAA AirVenture, Sun 'n Fun, an AOPA fly-in). Dates are samples — edit as needed."
              actions={
                <Button onClick={() => void seed()} loading={seeding} iconName="add-plus">
                  Seed sample events
                </Button>
              }
            >
              Quick start
            </Header>
          }
        >
          <Box variant="p" color="text-body-secondary">
            Or add one manually below. Users see active and upcoming events near them and can check
            in.
          </Box>
        </Container>

        <Container header={<Header variant="h2">Add an event</Header>}>
          <SpaceBetween size="m">
            <FieldGrid form={form} onField={field} />
            <Button
              variant="primary"
              onClick={() => void create()}
              loading={creating}
              disabled={!canCreate}
            >
              Create event
            </Button>
          </SpaceBetween>
        </Container>

        {error && <Alert type="error">{error}</Alert>}

        <Table
          loading={loading}
          loadingText="Loading events"
          items={events}
          columnDefinitions={[
            { id: 'name', header: 'Name', cell: (e) => e.name },
            {
              id: 'dates',
              header: 'Dates',
              cell: (e) =>
                e.start_date === e.end_date ? e.start_date : `${e.start_date} – ${e.end_date}`,
            },
            {
              id: 'location',
              header: 'Location',
              cell: (e) => e.location_name ?? e.airport_code ?? '—',
            },
            { id: 'status', header: 'Status', cell: (e) => e.status ?? '—' },
            {
              id: 'actions',
              header: '',
              cell: (e) => (
                <Button
                  variant="inline-link"
                  iconName="edit"
                  onClick={() => {
                    setEditForm(toForm(e));
                    setEditing(e);
                  }}
                >
                  Edit
                </Button>
              ),
            },
          ]}
          empty={<Box textAlign="center">No events yet — seed or add one above.</Box>}
          header={<Header counter={`(${events.length})`}>All events</Header>}
          variant="full-page"
        />
      </SpaceBetween>

      <Modal
        visible={editing !== null}
        onDismiss={() => setEditing(null)}
        header={`Edit ${editing?.name ?? 'event'}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={saving}
                disabled={!canSave}
                onClick={() => void save()}
              >
                Save changes
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FieldGrid form={editForm} onField={editField} />
          {editing && (
            <FormField
              label="Center & geo-fence"
              description="Click the map to move the event center. The circle shows the auto-tag radius."
            >
              <EventMap
                lat={mapLat}
                lng={mapLng}
                radiusKm={mapRadius}
                onPick={(la, ln) => setEditForm((f) => ({ ...f, lat: String(la), lng: String(ln) }))}
              />
            </FormField>
          )}
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
};

export default EventsPage;
